/**
 * Shared bulk-wine filename builders.
 * Used by the API (Content-Disposition header) and the admin frontend (fallback).
 * Single source of truth — do NOT duplicate this logic elsewhere.
 */

export interface BulkWineFilenameParts {
  code?: string | null;
  name?: string | null;
}

/**
 * Normalize a code for use in filenames.
 * Converts to uppercase and replaces non-alphanumeric characters with hyphens.
 * @throws {Error} If value is null, undefined, empty, or contains only invalid characters
 */
function normalizeCode(value: string | null | undefined): string {
  if (!value || value.trim().length === 0) {
    throw new Error('Bulk wine code or name is required to generate PDF filename');
  }

  const normalized = value
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .trim();

  if (normalized.length === 0) {
    throw new Error('Bulk wine code or name contains only invalid characters');
  }

  return normalized;
}

/**
 * Build a filename for a bulk wine specification sheet PDF.
 * Format: WineSpecSheet-<CODE>.pdf
 * 
 * @param bulkWine - Object containing code or name
 * @returns Filename in format "WineSpecSheet-<CODE>.pdf"
 * @throws {Error} If both code and name are missing, empty, or invalid
 */
export function buildBulkWineSpecSheetFilename(bulkWine: BulkWineFilenameParts): string {
  const code = normalizeCode(bulkWine.code || bulkWine.name);
  return `WineSpecSheet-${code}.pdf`;
}

/**
 * Generate a timestamped filename for bulk wine ZIP archives.
 * Used when exporting multiple bulk wines as a single ZIP file.
 * Single source of truth — do NOT duplicate this logic elsewhere.
 */
export function buildBulkWineZipFilename(): string {
  return `bulk-wines-${Date.now()}.zip`;
}
